import { SUPPORT_EMAIL } from '../supportContact'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export interface JdPrecheckResult {
  ok: boolean
  risk_tier: 'low' | 'medium' | 'high'
  risk_score: number
  blocked: boolean
  user_message?: string | null
  support_email?: string
  warnings?: string[]
}

export class JdPrecheckBlockedError extends Error {
  readonly riskTier: string
  readonly riskScore: number
  readonly supportEmail: string

  constructor(payload: {
    user_message?: string
    support_email?: string
    risk_tier?: string
    risk_score?: number
  }) {
    super(payload.user_message || 'Job description blocked for security reasons.')
    this.name = 'JdPrecheckBlockedError'
    this.riskTier = payload.risk_tier || 'high'
    this.riskScore = Number(payload.risk_score ?? 1)
    this.supportEmail = payload.support_email || SUPPORT_EMAIL
  }
}

export async function fetchJdPrecheck(params: {
  jobDescription: string
  accessToken: string
  source?: 'paste' | 'feedback'
}): Promise<JdPrecheckResult> {
  const response = await fetch(`${API_BASE_URL}/api/v1/optimization/jd-precheck`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${params.accessToken}`,
    },
    body: JSON.stringify({
      job_description: params.jobDescription,
      source: params.source ?? 'paste',
    }),
  })

  if (response.status === 422) {
    let payload: Record<string, unknown> = {}
    try {
      const body = (await response.json()) as { detail?: Record<string, unknown> }
      payload = (body.detail as Record<string, unknown>) || body
    } catch {
      payload = {}
    }
    throw new JdPrecheckBlockedError({
      user_message: String(payload.user_message || ''),
      support_email: String(payload.support_email || SUPPORT_EMAIL),
      risk_tier: String(payload.risk_tier || 'high'),
      risk_score: Number(payload.risk_score ?? 1),
    })
  }

  if (!response.ok) {
    const text = await response.text()
    throw new Error(text || `Security precheck failed (${response.status})`)
  }

  return (await response.json()) as JdPrecheckResult
}
